import React, { useContext, useMemo, useState } from 'react';
import { EmailAuthProvider, reauthenticateWithCredential, verifyBeforeUpdateEmail } from 'firebase/auth';
import TopBar from '../components/TopBar';
import { useNavigate } from 'react-router-dom';
import layoutStyles from '../styles/layout.module.css';
import styles from './Account.module.css';
import { UserContext } from '../App';
import { auth } from '../firebase';

function handleBack(navigate) {
  if (window.history.length > 2) navigate(-1);
  else navigate('/account');
}

function mapAuthError(error) {
  const code = String(error?.code || '').trim();
  if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') return 'Your current password is incorrect.';
  if (code === 'auth/email-already-in-use') return 'That email address is already in use.';
  if (code === 'auth/invalid-email') return 'Enter a valid email address.';
  if (code === 'auth/requires-recent-login') return 'Please sign in again, then retry this change.';
  return error?.message || 'Unable to update your email.';
}

export default function ChangeEmail() {
  const appUser = useContext(UserContext);
  const navigate = useNavigate();
  const [nextEmail, setNextEmail] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const currentEmail = useMemo(() => appUser?.email || auth.currentUser?.email || 'Not available', [appUser?.email]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedEmail = nextEmail.trim().toLowerCase();
    const currentUser = auth.currentUser;
    const hasPasswordProvider = currentUser?.providerData?.some((provider) => provider.providerId === 'password');

    if (!currentUser || !currentUser.email) {
      setError('You must be signed in with an email/password account to change your email.');
      return;
    }
    if (!hasPasswordProvider) {
      setError('This account does not use password sign-in, so email changes are not available here.');
      return;
    }
    if (!normalizedEmail) {
      setError('Enter your new email address.');
      return;
    }
    if (normalizedEmail === String(currentUser.email || '').trim().toLowerCase()) {
      setError('Enter a different email address.');
      return;
    }
    if (!password) {
      setError('Enter your current password to confirm this change.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);
      await verifyBeforeUpdateEmail(currentUser, normalizedEmail);
      setSuccess(`Verification sent to ${normalizedEmail}. Open that inbox and confirm the link before the change becomes active.`);
      setPassword('');
      setNextEmail('');
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar variant="back" backLabel="Back" onBack={() => handleBack(navigate)} rightActionLabel="Home" onRightAction={() => navigate('/home')} />
      <div className={`${layoutStyles.detailMain} ${styles.pageShell}`.trim()}>
        <div className={styles.pageInner}>
          <section className={styles.heroCard}>
            <span className={styles.eyebrow}>Account</span>
            <h1 className={styles.title}>Change Email</h1>
            <p className={styles.subtitle}>Protect your account by verifying the new email address before it replaces the current one.</p>
          </section>

          <section className={styles.sectionCard}>
            <div className={styles.sectionHeader}>
              <div>
                <span className={styles.sectionEyebrow}>Current Email</span>
                <h2 className={styles.sectionTitle}>{currentEmail}</h2>
              </div>
            </div>

            <form className={styles.form} onSubmit={handleSubmit}>
              <label className={styles.field}>
                <span>New Email Address</span>
                <input value={nextEmail} onChange={(event) => setNextEmail(event.target.value)} placeholder="name@example.com" autoComplete="email" inputMode="email" />
              </label>
              <label className={styles.field}>
                <span>Current Password</span>
                <input value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Current password" type="password" autoComplete="current-password" />
              </label>
              <p className={styles.helperText}>We will send a verification link to the new address. Your email stays unchanged until that link is confirmed.</p>
              {error ? <p className={styles.errorState}>{error}</p> : null}
              {success ? <p className={styles.successState}>{success}</p> : null}
              <div className={styles.actions}>
                <button type="button" className={styles.secondaryButton} onClick={() => navigate('/account')}>Back to Account</button>
                <button type="submit" className={styles.primaryButton} disabled={saving}>{saving ? 'Sending…' : 'Send Verification'}</button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}
